import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ApplicationIcon, Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_BLD, CF_SEC_BLD, testIds } from '../../constants';
import { type TableOptions, type TableRowType } from '../../types';
import { getLogoEndpointUrl } from '../../utils';
import MarkDown from '../MarkDown';

import './Table.css';

interface TableProps {
  headers: (string | null | undefined)[];
  rows: TableRowType[][];
  options?: TableOptions;
}

/**
 * Table
 */
function Table({
  headers,
  rows,
  options,
}: TableProps): React.ReactElement | null {
  const headerColCount = headers.length;
  const dataColCount = rows.length > 0 ? rows[0].length : 0;

  if (dataColCount === 0 || headerColCount === 0) {
    return null;
  }

  return (
    <Box tag='div' className={cx('Table')} dataTestId={testIds.GEN_TABLE_ID}>
      <div
        className='Table-headerRow'
        style={{ gridTemplateColumns: `repeat(${headerColCount}, 1fr)` }}>
        {headers.map((h, i) => {
          const key = `${i}`;

          if (typeof h === 'string') {
            return (
              <Typography key={key} kind='body3' color={CF_NEU_BLD}>
                {h.toUpperCase()}
              </Typography>
            );
          }

          return (
            <span key={key} style={{ color: 'transparent' }}>
              <FormattedMessage
                id='oZ0Rh9'
                defaultMessage='NA'
                description='Text to indicate not applicable header in the Table component when the header value is null or undefined'
              />
            </span>
          );
        })}
      </div>
      <Box tag='div' className='Table-bodyContainer'>
        {rows.map((row, idx) => {
          const key = `${idx}`;

          return (
            <div
              key={key}
              className='Table-bodyRow'
              style={{ gridTemplateColumns: `repeat(${dataColCount}, 1fr)` }}>
              {row.map((e, _idx) => {
                const innerKey = `${key}-${_idx}`;

                const isInskipList = options?.skipMarkdown?.includes(_idx);
                const isLogo = options?.logoCols?.includes(_idx);

                if (typeof e === 'string' && !isInskipList) {
                  return <MarkDown key={innerKey}>{e}</MarkDown>;
                }

                if (isInskipList && isLogo) {
                  const typedE = e as { logo: string; name: string };

                  return (
                    <Box
                      key={innerKey}
                      tag='div'
                      className='Table-bodyRow--isLogo'>
                      <ApplicationIcon
                        size='xxSmall'
                        src={getLogoEndpointUrl(typedE.logo)}
                      />
                      <Typography
                        kind='body2-bold'
                        color={CF_SEC_BLD}
                        lineLimit={1}>
                        {typedE.name}
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <span key={innerKey} style={{ color: 'transparent' }}>
                    <FormattedMessage
                      id='oZ0Rh9'
                      defaultMessage='NA'
                      description='Text to indicate not applicable header in the Table component when the header value is null or undefined'
                    />
                  </span>
                );
              })}
            </div>
          );
        })}
      </Box>
    </Box>
  );
}

Table.defaultProps = {
  options: undefined,
};

export default memo(Table);
